import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, MapPin, Network, Building2, Users, HeartHandshake, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSiteConfig } from '@/contexts/SiteConfigContext';

export function Ecosystem() {
  const { config: siteConfig } = useSiteConfig();
  const partners = (siteConfig as any)?.ecosystemPartners ?? [];
  const hubs = (siteConfig as any)?.ecosystemHubs ?? [];
  const events = (siteConfig as any)?.ecosystemEvents ?? [];
  const stats = (siteConfig as any)?.ecosystemStats ?? [];

  const fallbackHubs = [
    {
      name: 'Nairobi Innovation Hub',
      region: 'East Africa',
      location: 'Nairobi, Kenya',
      summary: 'The physical core where founders build, collaborate, and execute daily.',
      image: '/images/pexels-jamies-x-co-4242550.jpg',
    },
    {
      name: 'Virtual Campus',
      region: 'Remote-first',
      location: 'Pan-African',
      summary: 'Live sessions, office hours, product reviews, and async support across the continent.',
      image: '/images/kenyan_talent_learning.png',
    },
    {
      name: 'Partner Spaces',
      region: 'Distributed nodes',
      location: 'Universities and corporates',
      summary: 'A network of local nodes extending access to tools, talent, and mentorship.',
      image: '/images/kenyan_venture_programs.png',
    },
  ];
  const fallbackPartners = [
    { name: 'Investor Network', type: 'Investors', summary: 'Backing high-conviction founders emerging from Homeland.', image: '/images/kenyan_capital_partnerships.png' },
    { name: 'University Alliances', type: 'Universities', summary: 'Talent pipelines, research, and early-stage innovation.', image: '/images/uontowers.jpg' },
    { name: 'Corporate Partners', type: 'Corporates', summary: 'Pilots, distribution access, and sector expertise.', image: '/images/pexels-divinetechygirl-1181230.jpg' },
    { name: 'Public & Development Partners', type: 'Government & Development', summary: 'Policy alignment and large-scale deployment opportunities.', image: '/images/pexels-divinetechygirl-1181360.jpg' },
  ];
  const fallbackEvents = [
    { name: 'Founder Showcase', cadence: 'Bi-annual', summary: 'Ventures present traction and next steps to investors and partners.', image: '/images/pexels-japhet-kweba-84748259-12478615.jpg' },
    { name: 'Operator Circles', cadence: 'Monthly', summary: 'Closed-door sessions for product, growth, and operations leaders.', image: '/images/pexels-setmultimedia-15393153.jpg' },
    { name: 'Build Weeks', cadence: 'Quarterly', summary: 'Intensive execution sprints focused on shipping.', image: '/images/kenyan_talent_learning.png' },
    { name: 'Open Demo Days', cadence: 'Quarterly', summary: 'Public-facing demo events connecting ventures to the wider community.', image: '/images/kenyan_venture_programs.png' },
  ];

  const hubList = (hubs as any[]).length > 0 ? hubs : fallbackHubs;
  const partnerList = (partners as any[]).length > 0 ? partners : fallbackPartners;
  const eventList = (events as any[]).length > 0 ? events : fallbackEvents;
  const statList = (stats as any[]).length > 0 ? stats : [];

  return (
    <div className="relative overflow-hidden">
      {/* Overview */}
      <section className="py-20 md:py-28 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-africa-orange mb-3">
            The Homeland ecosystem
          </p>
          <h1 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-4">
            A connected engine for builders, ventures, and scale
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-8">
            Homeland is designed as a system, not isolated programs. Talent, execution, capital,
            and community flow together to produce compounding outcomes.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button
              asChild
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-gold-light rounded-xl"
            >
              <Link to="/programs">
                Discover programs <ArrowRight className="w-5 h-5 ml-1 inline" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-xl">
              <Link to="/contact">Become a partner</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* How system works */}
      <section className="py-14 md:py-18 px-6 bg-muted/40">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-africa-orange mb-2">How the ecosystem works</p>
            <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground">
              Value flows through connected loops
            </h2>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5 md:p-7">
            <div className="flex flex-wrap items-center gap-2 md:gap-3 text-xs md:text-sm">
              {['Talent', 'Programs', 'Ventures', 'Capital', 'Scale', 'Give back', 'Talent'].map((n, i) => (
                <div key={`${n}-${i}`} className="inline-flex items-center gap-2">
                  <span className="px-3 py-1.5 rounded-full border border-border bg-background text-foreground font-medium">{n}</span>
                  {i < 6 && <ArrowRight className="w-4 h-4 text-muted-foreground" />}
                </div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
              Builders enter through programs. Ideas become ventures. Ventures unlock partnerships
              and capital. Success feeds back into the system through mentorship, jobs, and new
              ventures — creating a compounding loop.
            </p>
          </div>
        </div>
      </section>

      {/* Hubs & communities */}
      <section className="py-14 md:py-18 px-6 bg-muted/40">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-africa-orange mb-2">
                Hubs & communities
              </p>
              <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground mb-2">
                Where the ecosystem comes to life
              </h2>
              <p className="text-sm text-muted-foreground max-w-xl">
                Highlight your physical and digital spaces – campuses, partner hubs, and community
                chapters – where Homeland shows up.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {hubList.map((h: any) => (
                  <div
                    key={h.name}
                    className="rounded-2xl border border-border bg-card/80 overflow-hidden"
                  >
                    {h.image && (
                      <div className="aspect-[16/10]">
                        <img src={h.image} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="p-5 space-y-2">
                      <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{h.region ?? 'Hub'}</p>
                      <h3 className="font-display font-semibold text-foreground text-lg">{h.name}</h3>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {h.location ?? 'Location TBD'}
                      </p>
                      {h.summary && <p className="text-sm text-muted-foreground leading-relaxed mt-2">{h.summary}</p>}
                      <div className="pt-2 text-xs text-muted-foreground">
                        Daily build sessions • Office hours • Founder standups • Product reviews
                      </div>
                    </div>
                  </div>
                ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-14 md:py-18 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-africa-orange mb-2">
                Strategic partners
              </p>
              <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground mb-2">
                Who powers the ecosystem
              </h2>
              <p className="text-sm text-muted-foreground max-w-xl">
                Homeland is not built alone. We work with institutions that bring capital,
                distribution, talent, and infrastructure.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {partnerList.map((p: any) => (
              <div
                key={p.name}
                className="rounded-2xl border border-border bg-card/80 overflow-hidden flex flex-col"
              >
                {p.image && (
                  <div className="aspect-[16/10]">
                    <img src={p.image} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-5 space-y-2 flex-1 flex flex-col">
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-1">
                    {p.type ?? 'Partner'}
                  </p>
                  <h3 className="font-display font-semibold text-foreground text-lg mb-1">{p.name}</h3>
                  {p.summary && <p className="text-sm text-muted-foreground leading-relaxed">{p.summary}</p>}
                  {p.link && (
                    <a
                      href={p.link}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-auto pt-3 inline-flex items-center gap-1 text-xs text-africa-orange hover:underline"
                    >
                      Visit site <ArrowRight className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 rounded-2xl border border-border bg-card/70 p-5">
            <h3 className="font-display font-semibold text-foreground mb-2">What partners get</h3>
            <p className="text-sm text-muted-foreground">
              Early access to high-quality ventures • innovation pipelines • market intelligence •
              talent access
            </p>
          </div>
        </div>
      </section>

      {/* Rhythms */}
      <section className="py-14 md:py-18 px-6 bg-muted/40">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-africa-orange mb-2">
                The rhythms of the ecosystem
              </p>
              <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground mb-2">
                Operating cadence that keeps momentum high
              </h2>
              <p className="text-sm text-muted-foreground max-w-xl">
                Demo days, founder dinners, office hours, and learning sprints – outline the key
                rhythms that make the ecosystem feel alive.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {eventList.map((e: any) => (
                  <div
                    key={e.name}
                    className="rounded-2xl border border-border bg-card/80 p-5 space-y-3"
                  >
                    {e.image && (
                      <div className="rounded-lg overflow-hidden border border-border">
                        <img src={e.image} alt="" className="w-full h-36 object-cover" />
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <h3 className="font-display font-semibold text-foreground text-lg">
                        {e.name}
                      </h3>
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <Calendar className="w-3 h-3" />
                        {e.cadence ?? 'Occasional'}
                      </span>
                    </div>
                    {e.summary && <p className="text-sm text-muted-foreground leading-relaxed">{e.summary}</p>}
                  </div>
                ))}
          </div>
        </div>
      </section>

      {/* Pathways */}
      <section className="py-14 md:py-18 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground mb-6">How to plug in</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Building2, title: 'For founders', copy: 'Apply to venture programs and start building with support.' },
              { icon: Users, title: 'For operators', copy: 'Join teams, contribute skills, and grow into leadership roles.' },
              { icon: HeartHandshake, title: 'For partners', copy: 'Collaborate on pilots, fund ventures, or support ecosystem growth.' },
              { icon: Network, title: 'For community', copy: 'Attend events, contribute knowledge, and stay connected.' },
            ].map((p) => (
              <div key={p.title} className="rounded-2xl border border-border bg-card p-5">
                <p.icon className="w-5 h-5 text-africa-orange mb-2" />
                <h3 className="font-display font-semibold text-foreground mb-1">{p.title}</h3>
                <p className="text-sm text-muted-foreground">{p.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Proof */}
      {statList.length > 0 && (
        <section className="py-14 md:py-18 px-6 bg-muted/40">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground mb-6">Ecosystem in action</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {statList.map((s: any, idx: number) => (
                <div key={`${s.label}-${idx}`} className="rounded-2xl border border-border bg-card p-5">
                  <p className="text-2xl font-display font-bold text-foreground">{s.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Geography */}
      <section className="py-14 md:py-18 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground mb-3">
              Built in Nairobi. Scaling across Africa.
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              Homeland is expanding through regional nodes, partner institutions, and global
              connections.
            </p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-africa-orange" /> East Africa (core)</li>
              <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-africa-blue" /> West Africa (emerging)</li>
              <li className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-africa-green" /> Global investor and diaspora links</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="aspect-[16/10] rounded-xl border border-border bg-muted/40 flex items-center justify-center text-sm text-muted-foreground">
              Africa expansion map visualization
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-display font-semibold text-foreground mb-4">
            Plug into the system
          </h2>
          <p className="text-sm md:text-base text-muted-foreground mb-6">
            Whether you are building, investing, or partnering — Homeland is designed for people
            who move.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-gold-light rounded-xl">
              <Link to="/courses">Apply as founder <ArrowRight className="w-5 h-5 ml-1 inline" /></Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-xl">
              <Link to="/contact">Partner with us</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-xl">
              <Link to="/ecosystem">Join community</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
